import { batch, createEffect, createMemo, mapArray, onCleanup, type Accessor } from "solid-js"
import { createStore } from "solid-js/store"
import type { Project } from "@mux/sdk/v2"
import type { ReturnTypeOfGlobalSync } from "./projects.types"
import type { ReturnTypeOfServer } from "./projects.types"
import { workspaceTitle } from "@/utils/workspace"
import { AVATAR_COLOR_KEYS, type AvatarColorKey, type LocalProject } from "../layout-model"

export function createLayoutProjects(input: {
  globalSync: ReturnTypeOfGlobalSync
  server: ReturnTypeOfServer
  /** Workspace IDs in the user's order; unlisted workspaces sort newest first. */
  order: Accessor<readonly string[]>
}) {
  const [colors, setColors] = createStore<Record<string, AvatarColorKey>>({})
  const colorRequested = new Map<string, AvatarColorKey>()

  function enrich(project: Project): LocalProject {
    const [childStore] = input.globalSync.child(project.id, { bootstrap: false })
    const local = childStore.projectMeta
    const name = project.name || local?.name || workspaceTitle(project.directory)
    const base: LocalProject = {
      ...project,
      name,
      icon: {
        url: project.icon?.url,
        override: project.icon?.override ?? childStore.icon,
        color: project.icon?.color,
      },
    }
    const localOverride = local?.name !== undefined || local?.commands?.start !== undefined ||
      local?.icon?.override !== undefined || local?.icon?.color !== undefined
    if (project.id !== "global" && !localOverride) return base
    return {
      ...base,
      name: base.name || local?.name,
      commands: local?.commands,
      icon: { url: base.icon?.url, override: local?.icon?.override, color: local?.icon?.color },
    }
  }

  const rootFor = (directory: string) => input.globalSync.data.project.find((project) =>
    project.directory === directory || project.id === directory || project.worktree === directory)?.id ?? directory

  createEffect(() => {
    const projects = input.server.projects.list()
    const seen = new Set(projects.map((project) => project.id))
    batch(() => {
      for (const project of projects) {
        const root = rootFor(project.directory)
        if (root === project.id) continue
        input.server.projects.close(project.id)
        if (!seen.has(root)) {
          const metadata = input.globalSync.data.project.find((item) => item.id === root)
          if (metadata?.directory) {
            input.server.projects.open(root, metadata.directory, project.name)
            seen.add(root)
          }
        }
        if (project.expanded) input.server.projects.expand(root)
      }
    })
  })

  const enriched = mapArray(() => input.globalSync.data.project, enrich)
  const sorted = createMemo(() => {
    const projects = enriched()
    const order = input.order()
    if (order.length === 0) return projects
    return [...projects].sort((a, b) => {
      const aIndex = order.indexOf(a.id)
      const bIndex = order.indexOf(b.id)
      if (aIndex === -1 && bIndex === -1) return (b.time?.created ?? 0) - (a.time?.created ?? 0)
      if (aIndex === -1) return -1
      if (bIndex === -1) return 1
      return aIndex - bIndex
    })
  })
  const list = mapArray(sorted, (project) => {
    const color = project.icon?.color ?? colors[project.worktree]
    return color ? { ...project, icon: { ...project.icon, color } } : project
  })

  createEffect(() => {
    const projects = enriched()
    if (!input.globalSync.ready || projects.length === 0) return
    const timer = setTimeout(() => {
      for (const project of projects) {
        if (project.id !== "global") input.globalSync.project.icon(project.worktree, project.icon?.override)
      }
    }, 1500)
    onCleanup(() => clearTimeout(timer))
  })

  createEffect(() => {
    const projects = enriched()
    if (!input.globalSync.ready || projects.length === 0) return
    const timer = setTimeout(() => {
      const used = new Set<string>()
      for (const project of projects) {
        if (project.icon?.color) colorRequested.delete(project.worktree)
        const color = project.icon?.color ?? colors[project.worktree]
        if (color) used.add(color)
      }
      for (const project of projects) {
        if (project.icon?.color) continue
        const available = AVATAR_COLOR_KEYS.filter((color) => !used.has(color))
        const color = colors[project.worktree] ?? available[Math.floor(Math.random() * available.length)] ??
          AVATAR_COLOR_KEYS[Math.floor(Math.random() * AVATAR_COLOR_KEYS.length)]
        if (!colors[project.worktree]) {
          used.add(color)
          setColors(project.worktree, color)
        }
        if (project.id === "global" && colorRequested.get(project.worktree) !== color) {
          colorRequested.set(project.worktree, color)
          input.globalSync.project.meta(project.worktree, { icon: { color } })
        }
      }
    }, 2000)
    onCleanup(() => clearTimeout(timer))
  })

  return { list }
}
